import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { PrivacyRequest } from '@nucleus-commerce/api-client/admin';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useStore } from '@/shared/store/store-context';

import { PrivacyRequestActionDialog } from '../components/privacy-request-action-dialog';
import {
  usePrivacyRequestColumns,
  type PrivacyRequestAction,
} from '../components/privacy-request-columns';
import { privacyRequestFilters } from '../components/privacy-request-filters';
import { useDownloadPrivacyExport } from '../hooks/use-download-privacy-export';
import { usePrivacyRequests } from '../hooks/use-privacy-requests';
import { useUpdatePrivacyRequest } from '../hooks/use-update-privacy-request';

const ROUTE_ID = '/_authed/_active/s/$storeId/privacy-requests' as const;
const ROUTE_PATH = '/s/$storeId/privacy-requests' as const;

interface Pending {
  request: PrivacyRequest;
  move: 'completed' | 'rejected';
}

function PrivacyRequests() {
  const { t } = useTranslation('privacyRequests');
  const store = useStore();
  const message = useErrorMessage();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const start = useUpdatePrivacyRequest(store.id, 'privacyRequests:toasts.started');
  const download = useDownloadPrivacyExport(store.id);

  const filters = useMemo(
    () => privacyRequestFilters({ t, queryClient, storeId: store.id }),
    [queryClient, store.id, t],
  );
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const query = usePrivacyRequests(store.id, list.params);

  const { mutate: startRequest } = start;
  const { mutate: downloadExport } = download;
  const onAction = useCallback(
    (action: PrivacyRequestAction, request: PrivacyRequest) => {
      if (action === 'download') downloadExport(request.id);
      else if (action === 'in_progress') {
        startRequest(
          { id: request.id, body: { status: 'in_progress' } },
          { onError: (error) => toast.error(message(error)) },
        );
      } else setPending({ request, move: action });
    },
    [downloadExport, message, startRequest],
  );

  const columns = usePrivacyRequestColumns(store.id, onAction);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'privacy-requests',
  });

  const narrowed = Object.keys(list.filters).length > 0 || Boolean(list.cursor);
  const isEmptyList = query.isSuccess && query.data.items.length === 0 && !narrowed;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name })}
        backTo={{ to: '/s/$storeId/customers', params: { storeId: store.id } }}
        backLabel={t('back')}
      />
      {isEmptyList ? (
        <EmptyState
          icon={ShieldCheck}
          title={t('empty.title')}
          description={t('empty.description')}
        />
      ) : (
        <DataTable
          table={table}
          search={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          getRowLabel={(row) => row.email}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
        />
      )}
      {pending ? (
        <PrivacyRequestActionDialog
          key={`${pending.request.id}:${pending.move}`}
          storeId={store.id}
          request={pending.request}
          move={pending.move}
          onOpenChange={(open) => {
            if (!open) setPending(null);
          }}
        />
      ) : null}
    </PageContent>
  );
}

export function PrivacyRequestsPage() {
  return (
    <FeatureErrorBoundary name="privacyRequests.list">
      <PrivacyRequests />
    </FeatureErrorBoundary>
  );
}
